import React, { useState } from "react";
import { History, ChevronDown, ChevronRight, ArrowRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";

const h = React.createElement;

const fmtDateTime = (d) => {
  if (!d) return "—";
  const date = new Date(d);
  return date.toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit"
  });
};

const fmtVal = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "object") {
    const s = JSON.stringify(v);
    return s.length > 80 ? s.slice(0, 80) + "…" : s;
  }
  if (typeof v === "string" && v.length > 80) return v.slice(0, 80) + "…";
  return String(v);
};

const ACTION_COLORS = {
  CREATE: "bg-emerald-50 text-emerald-700 border-emerald-200",
  EDIT: "bg-blue-50 text-blue-700 border-blue-200",
  AMEND: "bg-amber-50 text-amber-700 border-amber-200",
  STATUS_CHANGE: "bg-violet-50 text-violet-700 border-violet-200",
  CANCEL: "bg-rose-50 text-rose-700 border-rose-200",
  APPROVE: "bg-emerald-50 text-emerald-700 border-emerald-200",
  REJECT: "bg-rose-50 text-rose-700 border-rose-200",
};

function AuditTrail(props) {
  const entries = props.entries || [];
  const title = props.title || "Audit Trail";
  const className = props.className || "";
  const collapsedByDefault = props.collapsedByDefault !== false;
  const [openMap, setOpenMap] = useState({});

  const sorted = [...entries].sort((a, b) => (b.timestamp || "").localeCompare(a.timestamp || ""));
  const toggle = (i) => setOpenMap(m => ({ ...m, [i]: !m[i] }));

  const renderChangesTable = (changes) => h("div", { className: "bg-slate-50/60 rounded-lg border border-slate-100 overflow-hidden" },
    h("table", { className: "w-full text-xs" },
      h("thead", { className: "bg-slate-100/60" },
        h("tr", null,
          h("th", { className: "text-left px-3 py-1.5 font-semibold text-slate-600 w-1/3" }, "Field"),
          h("th", { className: "text-left px-3 py-1.5 font-semibold text-slate-600" }, "From"),
          h("th", { className: "w-6" }),
          h("th", { className: "text-left px-3 py-1.5 font-semibold text-slate-600" }, "To")
        )
      ),
      h("tbody", null,
        ...changes.map((c, ci) => h("tr", { key: ci, className: "border-t border-slate-100" },
          h("td", { className: "px-3 py-1.5 font-mono text-slate-700" }, c.field),
          h("td", { className: "px-3 py-1.5 text-rose-700 line-through opacity-70" }, fmtVal(c.from)),
          h("td", { className: "px-1 text-slate-300" }, h(ArrowRight, { className: "w-3 h-3" })),
          h("td", { className: "px-3 py-1.5 text-emerald-700 font-medium" }, fmtVal(c.to))
        ))
      )
    )
  );

  const renderEntry = (entry, idx) => {
    const isOpen = collapsedByDefault ? !!openMap[idx] : (openMap[idx] !== false);
    const action = entry.action || "EDIT";
    const colorCls = ACTION_COLORS[action] || "bg-slate-50 text-slate-700 border-slate-200";
    const changeCount = (entry.changes || []).length;
    return h("li", { key: idx, className: "ml-6 relative", "data-testid": "audit-entry-" + idx },
      h("span", {
        className: "absolute -left-[34px] flex items-center justify-center w-7 h-7 rounded-full ring-4 ring-white border " + colorCls
      }, h("span", { className: "text-[10px] font-bold" }, action[0])),
      h("div", { className: "flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2" },
        h("div", null,
          h("div", { className: "flex items-center gap-2 flex-wrap" },
            h(Badge, { className: "text-xs font-semibold border " + colorCls }, action),
            h("span", { className: "text-sm font-semibold text-slate-900" }, entry.actor_name || "Unknown"),
            entry.actor_role && h("span", { className: "text-xs text-slate-500" }, "(", entry.actor_role, ")"),
            changeCount > 0 && h("button", {
              type: "button", onClick: () => toggle(idx),
              className: "text-xs text-blue-600 hover:underline flex items-center gap-1"
            },
              isOpen ? h(ChevronDown, { className: "w-3 h-3" }) : h(ChevronRight, { className: "w-3 h-3" }),
              changeCount, " field", changeCount > 1 ? "s" : "", " changed"
            )
          ),
          entry.note && h("p", { className: "text-xs text-slate-500 italic mt-0.5" }, entry.note)
        ),
        h("p", { className: "text-xs text-slate-400 font-mono whitespace-nowrap" }, fmtDateTime(entry.timestamp))
      ),
      isOpen && changeCount > 0 && renderChangesTable(entry.changes)
    );
  };

  return h(Card, { className: "border-slate-200 " + className, "data-testid": "audit-trail" },
    h(CardHeader, { className: "pb-3 border-b border-slate-100" },
      h(CardTitle, { className: "text-sm font-semibold uppercase tracking-wider text-slate-700 flex items-center gap-2" },
        h(History, { className: "w-4 h-4" }),
        title,
        sorted.length > 0 && h("span", { className: "text-xs text-slate-400 font-normal" }, "(", sorted.length, ")")
      )
    ),
    h(CardContent, { className: "p-5" },
      sorted.length === 0
        ? h("div", { className: "text-sm text-slate-400 text-center py-6" },
            "No edits recorded yet. Future edits to this record will be tracked here."
          )
        : h("ol", { className: "relative border-l-2 border-slate-200 ml-3 space-y-5" },
            ...sorted.map(renderEntry)
          )
    )
  );
}

export default AuditTrail;
